import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Card, Col, Modal, Nav, Row, Spinner } from 'react-bootstrap';
import { FaGlobe, FaInbox, FaPaperPlane, FaPlus } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  createMensagem,
  getMensagemById,
  getMensagens,
  updateMensagem,
} from '../../services/mensagens';
import { selectProntuariosByPac } from '../../services/prontuario';
import { getFuncionarios } from '../../services/usuarios';
import { isNumericId } from '../../utils/numericId';
import MensagemDetalhe from './MensagemDetalhe';
import MensagemForm from './MensagemForm';
import MensagensList from './MensagensList';

const normalizarMensagem = (msg) => {
  if (!msg) return null;
  return {
    ...msg,
    CODMEDICO: msg.CODMEDICO ?? 0,
    CODPAC: msg.CODPAC ?? null,
    NOMEPAC: msg.PACIENTE?.NOMEPAC || msg.NOMEPAC || '',
    LEITURA: msg.LEITURA || 'N',
  };
};

const formatarEndereco = (pac) => {
  if (!pac) return '';
  const rua = pac.ENDRESPAC || '';
  const bairro = pac.BAIRROPAC || '';
  const cidade = pac.CIDADEPAC || '';
  const estado = pac.ESTADOPAC || '';
  const cep = pac.CEPPAC || '';
  const partesLocal = [rua, bairro, cidade].filter(Boolean).join(', ');
  const parteEstado = estado ? (partesLocal ? `/${estado}` : estado) : '';
  const parteCep = cep ? ` - CEP: ${cep}` : '';

  return `${partesLocal}${parteEstado}${parteCep}`.trim();
};

const extrairNomeFuncionario = (cod, lista) => {
  if (!cod) return '';
  const f = (lista || []).find((func) => String(func.codFunc) === String(cod));
  return f ? f.nome : cod;
};

const INITIAL_FORM_VALUES = {
  CODFUNCORIGEM: '',
  CODFUNCDESTINO: '',
  CODPAC: '',
  CODMEDICO: '',
  NOMEPAC: '',
  ENDERECOPAC: '',
  MENSAGEM: '',
  LEITURA: 'N',
  TIPO_PEDIDO: 'RECEITA',
  PRIORIDADE: 'NORMAL',
  STATUS: 'PENDENTE',
};

const GerenciamentoMensagensContainer = forwardRef(
  ({ show, onHide, contexto, codFuncDestino }, ref) => {
    const history = useHistory();
    const [mensagens, setMensagens] = useState([]);
    const [selecionada, setSelecionada] = useState(null);
    const [formAberto, setFormAberto] = useState(false);
    const [abaAtiva, setAbaAtiva] = useState('recebidas'); // 'recebidas' | 'enviadas' | 'todas'
    const [formValores, setFormValores] = useState({
      ...INITIAL_FORM_VALUES,
      CODFUNCORIGEM: codFuncDestino || '',
      CODFUNCDESTINO: '',
    });
    const [editando, setEditando] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const [funcionarios, setFuncionarios] = useState([]);

    const resetForm = useCallback(() => {
      setFormValores({
        ...INITIAL_FORM_VALUES,
        CODFUNCORIGEM: codFuncDestino || '',
        CODFUNCDESTINO: '',
      });
      setEditando(false);
      setFormAberto(false);
    }, [codFuncDestino]);

    const fetchFuncionarios = useCallback(async () => {
      try {
        const data = await getFuncionarios();
        setFuncionarios(data || []);
      } catch (e) {
        console.error('Erro ao buscar funcionários:', e);
        toast.error('Erro ao carregar lista de funcionários.');
        setFuncionarios([]);
      }
    }, []);

    // Busca ajustada para atender aos parâmetros de cada aba
    const fetchMensagens = useCallback(async () => {
      if (!codFuncDestino && abaAtiva !== 'todas') {
        setMensagens([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let params = {};

        if (abaAtiva === 'recebidas') {
          // Aba 1: Recebidas e NÃO LIDAS
          params = {
            codfuncdestino: codFuncDestino,
            leitura: 'N',
          };
        } else if (abaAtiva === 'enviadas') {
          // Aba 2: Enviadas por mim (Lidas e Não Lidas)
          params = {
            codfuncorigem: codFuncDestino,
          };
        } else if (abaAtiva === 'todas') {
          // Aba 3: Todas as mensagens da clínica
          params = {
            modo: 'todas',
            limite: 200,
          };
        }

        const data = await getMensagens(params);
        const normalizadas = (data || []).map(normalizarMensagem);
        setMensagens(normalizadas);

        setSelecionada((prev) => {
          if (!prev) return null;
          const atualizada = normalizadas.find((m) => m.CODMENSAGEM === prev.CODMENSAGEM);
          return atualizada || null;
        });
      } catch (e) {
        console.error('Erro ao buscar mensagens:', e);
        toast.error('Erro ao carregar lista de mensagens.');
        setMensagens([]);
      } finally {
        setLoading(false);
      }
    }, [codFuncDestino, abaAtiva]);

    useImperativeHandle(
      ref,
      () => ({
        reloadMensagens: fetchMensagens,
      }),
      [fetchMensagens]
    );

    useEffect(() => {
      if (show) {
        if (funcionarios.length === 0) fetchFuncionarios();
        fetchMensagens();
      }
    }, [show, fetchFuncionarios, fetchMensagens, funcionarios.length]);

    const handleSelectMensagem = (msg) => {
      setSelecionada(normalizarMensagem(msg));
      setFormAberto(false);
    };

    const handleNova = () => {
      let NOMEPAC = '';
      let ENDERECOPAC = '';
      let CODPAC = '';
      let CODMEDICO = '';

      if (contexto?.agenda) {
        const ag = contexto.agenda;
        NOMEPAC = ag.NOMEPAC || '';
        CODPAC = ag.CODPAC || '';
        CODMEDICO = ag.CODMED || '';
        ENDERECOPAC = formatarEndereco(ag);
      }

      setFormValores({
        ...INITIAL_FORM_VALUES,
        CODFUNCORIGEM: codFuncDestino || '',
        CODFUNCDESTINO: '',
        CODPAC,
        NOMEPAC,
        ENDERECOPAC,
        CODMEDICO,
      });
      setEditando(false);
      setFormAberto(true);
    };

    const handleEditar = async () => {
      if (!selecionada) return;
      const msgNorm = normalizarMensagem(selecionada);

      setLoadingAcao(true);
      try {
        const msgApi = await getMensagemById(msgNorm.CODMENSAGEM);
        const m = normalizarMensagem(msgApi);
        setFormValores({
          CODFUNCORIGEM: m.CODFUNCORIGEM,
          CODFUNCDESTINO: m.CODFUNCDESTINO,
          CODPAC: m.CODPAC,
          NOMEPAC: m.NOMEPAC,
          ENDERECOPAC: formatarEndereco(m),
          CODMEDICO: m.CODMEDICO,
          MENSAGEM: m.MENSAGEM,
          LEITURA: m.LEITURA,
          TIPO_PEDIDO: m.TIPO_PEDIDO,
          PRIORIDADE: m.PRIORIDADE,
          STATUS: m.STATUS,
        });
      } catch {
        setFormValores({
          CODFUNCORIGEM: msgNorm.CODFUNCORIGEM,
          CODFUNCDESTINO: msgNorm.CODFUNCDESTINO,
          CODPAC: msgNorm.CODPAC,
          NOMEPAC: msgNorm.NOMEPAC,
          ENDERECOPAC: formatarEndereco(msgNorm),
          CODMEDICO: msgNorm.CODMEDICO,
          MENSAGEM: msgNorm.MENSAGEM,
          LEITURA: msgNorm.LEITURA,
          TIPO_PEDIDO: msgNorm.TIPO_PEDIDO,
          PRIORIDADE: msgNorm.PRIORIDADE,
          STATUS: msgNorm.STATUS,
        });
      } finally {
        setLoadingAcao(false);
      }
      setEditando(true);
      setFormAberto(true);
    };

    const handleResponder = async () => {
      if (!selecionada) return;
      const msg = normalizarMensagem(selecionada);
      const tipoPedido = msg.TIPO_PEDIDO;
      const codPac = msg.CODPAC;

      if (
        ['RECEITA', 'ATESTADO', 'PEDIDO_EXAME', 'REQUISICAO', 'PEDIDO'].includes(tipoPedido) &&
        isNumericId(codPac)
      ) {
        setLoadingAcao(true);
        try {
          const prontuarios = await selectProntuariosByPac(codPac);
          if (!prontuarios || prontuarios.length === 0) {
            toast.warn('Nenhum prontuário encontrado para o paciente.');
            return;
          }

          const abaPorPedido = {
            RECEITA: 'receita',
            ATESTADO: 'atestado',
            PEDIDO_EXAME: 'requisicao',
            REQUISICAO: 'requisicao',
            PEDIDO: 'requisicao',
          };
          const aba = abaPorPedido[tipoPedido];

          history.push(`/prontuario/${codPac}`, { aba });
          onHide?.();
          return;
        } catch (e) {
          console.error('Erro ao buscar prontuário:', e);
          toast.error('Erro ao buscar prontuário do paciente.');
        } finally {
          setLoadingAcao(false);
        }
      }

      const origCod = codFuncDestino || msg.CODFUNCDESTINO;
      const destCod = msg.CODFUNCORIGEM;

      const nomeOrigem = msg.FO_NOMEFUNC || extrairNomeFuncionario(msg.CODFUNCORIGEM, funcionarios);
      const nomeDestino =
        msg.FD_NOMEFUNC || extrairNomeFuncionario(msg.CODFUNCDESTINO, funcionarios);

      const corpo =
        `De: ${nomeOrigem}\n` +
        `Para: ${nomeDestino}\n\n` +
        `--- Resposta à mensagem original ---\n`;

      setFormValores({
        CODFUNCORIGEM: origCod,
        CODFUNCDESTINO: destCod,
        CODPAC: msg.CODPAC,
        NOMEPAC: msg.NOMEPAC,
        ENDERECOPAC: formatarEndereco(msg),
        CODMEDICO: msg.CODMEDICO,
        MENSAGEM: corpo,
        LEITURA: 'N',
        TIPO_PEDIDO: msg.TIPO_PEDIDO,
        PRIORIDADE: msg.PRIORIDADE,
        STATUS: msg.STATUS,
      });
      setEditando(false);
      setFormAberto(true);
    };

    const handleEncaminhar = () => {
      if (!selecionada) return;
      const msg = normalizarMensagem(selecionada);

      const nomeOrigem = msg.FO_NOMEFUNC || extrairNomeFuncionario(msg.CODFUNCORIGEM, funcionarios);

      const corpoEncaminhado =
        `--- Mensagem Encaminhada ---\n` +
        `De: ${nomeOrigem}\n` +
        `Data Original: ${msg.DATA_FORMATADA || ''}\n\n` +
        `Mensagem Original:\n${msg.MENSAGEM || ''}\n\n`;

      setFormValores({
        CODFUNCORIGEM: codFuncDestino || '',
        CODFUNCDESTINO: '',
        CODPAC: msg.CODPAC,
        NOMEPAC: msg.NOMEPAC,
        ENDERECOPAC: formatarEndereco(msg),
        CODMEDICO: msg.CODMEDICO,
        MENSAGEM: corpoEncaminhado,
        LEITURA: 'N',
        TIPO_PEDIDO: msg.TIPO_PEDIDO,
        PRIORIDADE: msg.PRIORIDADE,
        STATUS: 'PENDENTE',
      });
      setEditando(false);
      setFormAberto(true);
    };

    const handleSubmit = async (valoresForm) => {
      setLoadingAcao(true);
      try {
        if (editando && selecionada) {
          await updateMensagem(selecionada.CODMENSAGEM, valoresForm);
          toast.success('Mensagem atualizada com sucesso!');
        } else {
          await createMensagem(valoresForm);
          toast.success('Mensagem enviada com sucesso!');
        }
        await fetchMensagens();
        resetForm();
      } catch (e) {
        toast.error(editando ? 'Erro ao atualizar mensagem.' : 'Erro ao enviar mensagem.');
      } finally {
        setLoadingAcao(false);
      }
    };

    const handleMarcarComoLida = async (msgRaw) => {
      const msgNorm = normalizarMensagem(msgRaw);
      try {
        await updateMensagem(msgNorm.CODMENSAGEM, {
          CODMENSAGEM: msgNorm.CODMENSAGEM,
          CODFUNCORIGEM: msgNorm.CODFUNCORIGEM,
          CODFUNCDESTINO: msgNorm.CODFUNCDESTINO,
          CODMEDICO: msgNorm.CODMEDICO,
          CODPAC: msgNorm.CODPAC,
          MENSAGEM: msgNorm.MENSAGEM,
          TIPO_PEDIDO: msgNorm.TIPO_PEDIDO,
          PRIORIDADE: msgNorm.PRIORIDADE,
          STATUS: msgNorm.STATUS,
          LEITURA: 'S',
        });
        toast.info('Mensagem marcada como lida.');
        await fetchMensagens();
      } catch (e) {
        // console.error('Erro ao marcar como lida:', e);
        toast.error('Erro ao marcar mensagem como lida.');
      }
    };

    return (
      <>
        <Modal show={show} onHide={onHide} size="xl" centered className="modal-fullscreen-lg-down">
          <Modal.Header closeButton className="bg-light">
            <Modal.Title as="h5" className="fw-bold">
              Gerenciamento de Mensagens Internas
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Card className="h-100 shadow-sm border">
                  <Card.Header className="bg-light p-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleNova}
                        disabled={loadingAcao}
                        className="d-flex align-items-center gap-1 ms-auto"
                      >
                        <FaPlus size={12} /> Nova Mensagem
                      </Button>
                    </div>

                    {/* Navegação por Abas */}
                    <Nav variant="tabs" activeKey={abaAtiva} onSelect={(k) => setAbaAtiva(k)}>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="recebidas"
                          className="d-flex align-items-center gap-1 py-1 px-2 small"
                        >
                          <FaInbox size={12} /> Não Lidas
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="enviadas"
                          className="d-flex align-items-center gap-1 py-1 px-2 small"
                        >
                          <FaPaperPlane size={12} /> Enviadas
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="todas"
                          className="d-flex align-items-center gap-1 py-1 px-2 small"
                        >
                          <FaGlobe size={12} /> Todas (Mural)
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Header>

                  <Card.Body className="p-2">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" role="status" />
                        <p className="mt-2 text-muted small mb-0">Carregando mensagens...</p>
                      </div>
                    ) : (
                      <MensagensList
                        mensagens={mensagens}
                        onSelect={handleSelectMensagem}
                        funcionarios={funcionarios}
                        onMarcarComoLida={handleMarcarComoLida}
                        abaAtiva={abaAtiva}
                      />
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="h-100 shadow-sm border">
                  <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold text-secondary">
                      {formAberto
                        ? editando
                          ? 'Editar Mensagem'
                          : 'Nova Mensagem'
                        : selecionada
                          ? 'Detalhes da Mensagem'
                          : 'Selecione uma Mensagem'}
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {loadingAcao ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="secondary" role="status" />
                        <p className="mt-2 text-muted small mb-0">Processando requisição...</p>
                      </div>
                    ) : formAberto ? (
                      <MensagemForm
                        valores={formValores}
                        onChange={setFormValores}
                        onSubmit={handleSubmit}
                        destinatarios={funcionarios}
                        editando={editando}
                        onFechar={resetForm}
                        codFuncLogado={codFuncDestino}
                      />
                    ) : selecionada ? (
                      <MensagemDetalhe
                        mensagem={selecionada}
                        onResponder={handleResponder}
                        onEditar={handleEditar}
                        onEncaminhar={handleEncaminhar}
                        onHide={onHide}
                        funcionarios={funcionarios}
                      />
                    ) : (
                      <div className="text-center text-muted py-5">
                        <p className="mb-0">
                          Selecione uma mensagem da lista ao lado para visualizar os detalhes.
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
        </Modal>
      </>
    );
  }
);

export default GerenciamentoMensagensContainer;
